import type { LoopFormState } from "@/hooks/use-loop-form";
import { CADC, USDC } from "@/config/tokens";
import { formatToken } from "@/lib/format";
import { formatHealthFactor } from "@/lib/loop/risk";

type Props = {
  readonly form: LoopFormState;
  readonly connected: boolean;
};

type Tone = "ink" | "jade" | "rose";

type Row = {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: Tone;
};

const toneColor = (tone?: Tone): string => {
  if (tone === "jade") return "var(--accent-jade)";
  if (tone === "rose") return "var(--accent-rose)";
  return "var(--ink-1)";
};

const hfTone = (hf: number | undefined): Tone => {
  if (hf == null || !Number.isFinite(hf)) return "ink";
  if (hf < 1.1) return "rose";
  if (hf < 1.5) return "ink";
  return "jade";
};

const fmtLiq = (p: number | null): string => {
  if (p == null) return "—";
  return `$${p.toFixed(4)} / ${CADC.symbol}`;
};

export const ExistingPosition = ({ form, connected }: Props) => {
  const {
    venue,
    existingCollateralRaw,
    existingBorrowRaw,
    currentRisk,
    projectedRisk,
  } = form;
  const isMorpho = venue === "morpho";

  const eyebrow = !connected
    ? "Connect wallet"
    : currentRisk
      ? isMorpho
        ? "Live · Morpho"
        : "Live · Ajna"
      : "Loading…";

  const collateralStr =
    existingCollateralRaw == null
      ? "—"
      : `${formatToken(existingCollateralRaw, CADC.decimals, 2)} ${CADC.symbol}`;
  const borrowStr =
    existingBorrowRaw == null
      ? "—"
      : `${formatToken(existingBorrowRaw, USDC.decimals, 2)} ${USDC.symbol}`;

  const hfNow = currentRisk?.healthFactor;
  const hfNext = projectedRisk?.healthFactor;
  const liqNow = currentRisk?.liquidationPrice ?? null;
  const liqNext = projectedRisk?.liquidationPrice ?? null;

  const rows: Row[] = [
    { label: "Total collateral", value: collateralStr },
    { label: "Total borrowed", value: borrowStr },
    {
      label: "Health factor",
      value: hfNow == null ? "—" : formatHealthFactor(hfNow),
      hint:
        hfNext == null
          ? undefined
          : `post-loop · ${formatHealthFactor(hfNext)}`,
      tone: hfTone(hfNext ?? hfNow),
    },
    {
      label: "Liquidation price",
      value: fmtLiq(liqNow),
      hint: liqNext == null ? undefined : `post-loop · ${fmtLiq(liqNext)}`,
    },
  ];

  return (
    <section>
      <div className="flex items-baseline justify-between mb-4">
        <h3>Existing position</h3>
        <span
          className="font-mono text-[10px] tracking-[0.16em] uppercase"
          style={{ color: "var(--ink-4)" }}
        >
          {eyebrow}
        </span>
      </div>
      <ul className="space-y-3">
        {rows.map((r, i) => (
          <li
            key={r.label}
            className="flex items-baseline justify-between gap-6 pb-3"
            style={{
              borderBottom:
                i === rows.length - 1 ? "none" : "1px dashed var(--line-1)",
            }}
          >
            <span
              className="font-sans text-[12px] tracking-[0.04em]"
              style={{ color: "var(--ink-3)" }}
            >
              {r.label}
            </span>
            <span className="text-right">
              <span
                className="font-mono tabular text-[14px]"
                style={{ color: toneColor(r.tone), fontWeight: 400 }}
              >
                {r.value}
              </span>
              {r.hint && (
                <span
                  className="block font-sans text-[10px] tracking-[0.08em] uppercase mt-1"
                  style={{ color: "var(--ink-4)" }}
                >
                  {r.hint}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
